// ─────────────────────────────────────────────────────────────────────────────
// Tariff engine — pure logic over the §4 master data (./data.ts).
//
// Every function is pure and side-effect free, so the engine is fully unit
// testable without a database or any external system. It is applied at step 1
// (view), step 2 (export) and step 7 (PR overview).
// ─────────────────────────────────────────────────────────────────────────────
import { TARIFF_MATRIX, CONTRACTOR_CANONICAL_MAP, QUADRANT_PREFIX_RULES } from './data.js';
/**
 * Map a raw `SSV Contractor` value (or a DB `Contractor.code`) to its §4
 * {@link TariffColumn}. Case- and whitespace-insensitive. Returns `null` for
 * out-of-scope contractors (Walfit/Jacops/Unifiber), unknown values, and `null`
 * — i.e. "do not price this record".
 */
export function canonicalizeContractor(input) {
    if (input == null)
        return null;
    const key = input.trim().toLowerCase();
    if (key.length === 0)
        return null;
    return CONTRACTOR_CANONICAL_MAP[key] ?? null;
}
/**
 * Does a normalized (already trimmed, lower-cased, q-stripped) quadrant match a rule?
 * `sdu` is a TOKEN match (startsWith — covers `SDU/small MDU`). A single-letter code
 * matches only when the letter reaches a terminator — end-of-string, a digit, or `(` —
 * optionally through one `x` suffix (`cx`, `ex`) — so a real quadrant code (`c`, `cx`,
 * `c2`, `c(3)`, `e`, `ex`) matches but an arbitrary word (`cancelled`, `pending`,
 * `exception`) does NOT (→ UNRESOLVED; we never force-price free text).
 */
function matchesQuadrantRule(quadrant, prefix) {
    if (!quadrant.startsWith(prefix))
        return false;
    if (prefix.length > 1)
        return true; // multi-char token, e.g. 'sdu'
    // The letter must reach a terminator (end / digit / `(`), optionally via ONE `x` suffix
    // (`Cx`/`Ex`). Consuming the `x` BEFORE requiring a terminator is what rejects free text
    // like `EXCEPTION`/`EXISTING` (letter + `x` + more letters) — a bare `next === 'x'` would
    // force-price those once `e` became a priced prefix (D1, 2026-07-08).
    let i = prefix.length;
    if (quadrant.charAt(i) === 'x')
        i += 1; // optional single `x` suffix
    const next = quadrant.charAt(i);
    return next === '' || next === '(' || (next >= '0' && next <= '9');
}
/**
 * Resolve a record's service-type row (Gerd's confirmed mapping — GERD_ANSWERS_2026-06.md).
 *
 * Precedence (highest to lowest):
 *   1. stream === 'TSA'        → 'Signed TSA' (wins even if isEarlyEnd is also set —
 *                                 a state that should not occur, but TSA has priority).
 *   2. isEarlyEnd === true (SSV) → 'Early end visit'.
 *   3. a PRESENT empty/blank quadrant (SSV) → 'Basic site visit' (Gerd: empty/blank →
 *                                 Basic) — the literal tokens "empty"/"blank" or an
 *                                 empty/whitespace string.
 *   4. designQuadrant (SSV)    → service type by quadrant code, after normalizing:
 *                                 trim, lower-case, strip an optional leading `q`
 *                                 (`Dx` and `QD`/`QC2` both occur), then match a rule
 *                                 (see {@link matchesQuadrantRule}). `E`/`QE` maps to
 *                                 Indoor ≤5F, the same row as `C`/`QC` (decision D1, 2026-07-08).
 *   5. otherwise               → typed `UNRESOLVED_QUADRANT` — a truly-unknown value
 *                                 (`ONLY BUDI`, free text) OR a NULL (absent/unknown)
 *                                 quadrant such as a synthetic manually-added row:
 *                                 never fabricate a price where there is no data.
 *
 * `input.floors` is intentionally NOT consulted: the Design-Quadrant letter is primary.
 */
export function resolveServiceType(input) {
    if (input.stream === 'TSA') {
        return { ok: true, serviceType: 'Signed TSA' };
    }
    // SSV stream from here on.
    if (input.isEarlyEnd === true) {
        return { ok: true, serviceType: 'Early end visit' };
    }
    // A NULL quadrant is "unknown" (e.g. a synthetic manually-added row) → fall through
    // to UNRESOLVED; never fabricate a price. A PRESENT value (incl. empty/blank) is
    // classified below.
    if (input.designQuadrant !== null) {
        const raw = input.designQuadrant.trim().toLowerCase();
        // Present empty / blank value → Basic site visit (Gerd 2026-06): the literal
        // tokens "empty"/"blank", or an empty/whitespace-only string.
        if (raw === '' || raw === 'empty' || raw === 'blank') {
            return { ok: true, serviceType: 'Basic site visit' };
        }
        // Strip an optional leading `q` (Power-BI prefix: `QD`/`QC2` → `d`/`c2`); the bare
        // form (`dx`) passes through. Then match the quadrant code against the rules.
        const quadrant = raw.startsWith('q') ? raw.slice(1) : raw;
        for (const rule of QUADRANT_PREFIX_RULES) {
            if (matchesQuadrantRule(quadrant, rule.prefix)) {
                return { ok: true, serviceType: rule.serviceType };
            }
        }
    }
    return { ok: false, reason: 'UNRESOLVED_QUADRANT', designQuadrant: input.designQuadrant };
}
/**
 * Look up the rate (in integer cents) for a resolved service type × contractor
 * column. Total: both arguments are typed unions, so every cell exists.
 */
export function getRate(serviceType, column) {
    return TARIFF_MATRIX[serviceType][column];
}
/**
 * Compute the tariff for a record end to end. The contractor scope is checked
 * BEFORE the quadrant, so an out-of-scope contractor short-circuits to
 * `OUT_OF_SCOPE` even when the quadrant would also be unresolved.
 */
export function computeTariff(input) {
    const column = canonicalizeContractor(input.contractor);
    if (column === null) {
        return { status: 'OUT_OF_SCOPE', contractor: input.contractor };
    }
    const resolved = resolveServiceType(input);
    if (!resolved.ok) {
        return {
            status: 'UNRESOLVED',
            reason: resolved.reason,
            designQuadrant: resolved.designQuadrant,
        };
    }
    return {
        status: 'PRICED',
        amountCents: getRate(resolved.serviceType, column),
        serviceType: resolved.serviceType,
        column,
    };
}
/**
 * Format integer cents as a euro string, e.g. `2500` → `"€25.00"`.
 *
 * Guards against programming errors: a non-integer argument (which includes
 * `NaN` and `Infinity`, for which `Number.isInteger` is false) would render
 * garbage like `"€NaN"` or `"€25.005"`, so it throws instead. Negative cents are
 * allowed (e.g. future credit adjustments).
 */
export function formatCents(cents) {
    if (!Number.isInteger(cents)) {
        throw new RangeError(`formatCents expects integer cents, received: ${cents}`);
    }
    return `€${(cents / 100).toFixed(2)}`;
}
//# sourceMappingURL=engine.js.map